import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, messageErreur } from '../../lib/api'
import { Champ, Chargement, Message, Modale } from '../base'
import type { Cellule } from './Biologie'

type Parametre = {
  code: string
  libelle: string
  categorie: string
  uniteCible: string
  unitesReconnues: string[]
  conversions: Record<string, number>
  conversionsIncertaines: string[]
  note: string | null
}

/** Même normalisation que le serveur, pour l'aperçu de conversion. */
function normaliserUnite(unite: string): string {
  return unite
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/µ|μ/g, 'u')
    .replace(/\s+/g, '')
    .replace(/,/g, '.')
}

function normaliserLibelle(libelle: string): string {
  return libelle
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/œ/g, 'oe')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export default function FormulaireResultat({
  patientId,
  resultat,
  libelle,
  fermer,
}: {
  patientId: string
  resultat?: Cellule
  libelle?: string
  fermer: () => void
}) {
  const client = useQueryClient()
  const { data: table } = useQuery({
    queryKey: ['parametres-biologie'],
    queryFn: () => api<{ parametres: Parametre[] }>('/api/biologie/parametres'),
    staleTime: 60 * 60 * 1000,
  })

  const [valeurs, setValeurs] = useState({
    parametreLibelle: libelle ?? '',
    valeur: resultat
      ? (resultat.valeurTexte ??
        `${resultat.comparateur ?? ''}${resultat.valeurOriginale ?? resultat.valeurSi ?? ''}`)
      : '',
    unite: resultat?.uniteOriginale ?? '',
    refBasse: resultat?.refOriginale?.basse != null ? String(resultat.refOriginale.basse) : '',
    refHaute: resultat?.refOriginale?.haute != null ? String(resultat.refOriginale.haute) : '',
    datePrelevement: '',
    heure: '',
    laboratoire: resultat?.laboratoire ?? '',
    raison: '',
  })
  const [erreur, setErreur] = useState<string | null>(null)

  const parametre = useMemo(() => {
    if (!table) return null
    const cle = normaliserLibelle(valeurs.parametreLibelle)
    return (
      table.parametres.find((p) => normaliserLibelle(p.libelle) === cle) ??
      table.parametres.find((p) => normaliserLibelle(p.code) === cle) ??
      null
    )
  }, [table, valeurs.parametreLibelle])

  // Aperçu de la conversion, calculé avec la table servie par l'API.
  const apercu = useMemo(() => {
    if (!parametre) {
      return valeurs.parametreLibelle
        ? "Paramètre absent de la table : le résultat sera conservé tel quel, sans conversion."
        : null
    }
    const brut = valeurs.valeur.trim().replace(/^[<>]=?\s*/, '').replace(/\s/g, '').replace(',', '.')
    const nombre = Number.parseFloat(brut)
    if (!Number.isFinite(nombre) || !/^[0-9.]+$/.test(brut)) {
      return 'Résultat non chiffré : il sera conservé tel quel, sans conversion.'
    }
    const facteur = parametre.conversions[normaliserUnite(valeurs.unite)]
    if (facteur === undefined) {
      return `Unité non prévue pour ${parametre.libelle} : aucune conversion ne sera appliquée. Unités reconnues : ${parametre.unitesReconnues.join(', ')}.`
    }
    const converti = Number.parseFloat((nombre * facteur).toPrecision(6))
    const incertaine = parametre.conversionsIncertaines.includes(normaliserUnite(valeurs.unite))
    return `Sera enregistré comme ${converti} ${parametre.uniteCible}${
      facteur === 1 ? '' : ` (× ${facteur})`
    }.${incertaine ? ` ${parametre.note ?? 'Conversion à vérifier.'}` : ''}`
  }, [parametre, valeurs.valeur, valeurs.unite, valeurs.parametreLibelle])

  const enregistrer = useMutation({
    mutationFn: () => {
      const corps = {
        parametreLibelle: valeurs.parametreLibelle,
        valeur: valeurs.valeur,
        unite: valeurs.unite,
        refBasse: valeurs.refBasse,
        refHaute: valeurs.refHaute,
        datePrelevement: valeurs.datePrelevement,
        heure: valeurs.heure || null,
        laboratoire: valeurs.laboratoire || null,
        ...(resultat ? { raison: valeurs.raison || undefined } : {}),
      }
      return resultat
        ? api(`/api/resultats/${resultat.donneeId}`, { methode: 'PATCH', corps })
        : api(`/api/patients/${patientId}/resultats`, { methode: 'POST', corps })
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['biologie', patientId] })
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const champ = (cle: keyof typeof valeurs) => ({
    value: valeurs[cle],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setValeurs((v) => ({ ...v, [cle]: e.target.value })),
  })

  if (!table) {
    return (
      <Modale titre="Résultat de biologie" fermer={fermer}>
        <Chargement />
      </Modale>
    )
  }

  return (
    <Modale
      titre={resultat ? 'Corriger le résultat' : 'Saisir un résultat'}
      sousTitre={
        resultat
          ? "L'ancienne valeur restera consultable dans l'historique."
          : 'Saisis la valeur telle qu’elle est écrite sur le compte rendu : la conversion est faite par la table de l’application.'
      }
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => enregistrer.mutate()}
            disabled={
              enregistrer.isPending ||
              !valeurs.parametreLibelle.trim() ||
              !valeurs.valeur.trim() ||
              !valeurs.datePrelevement
            }
          >
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ
          etiquette="Paramètre *"
          className="sm:col-span-2"
          aide={parametre ? `Reconnu : ${parametre.libelle} (${parametre.uniteCible})` : undefined}
        >
          <input className="champ" list="parametres-biologie" {...champ('parametreLibelle')} />
          <datalist id="parametres-biologie">
            {table.parametres.map((p) => (
              <option key={p.code} value={p.libelle}>
                {p.categorie}
              </option>
            ))}
          </datalist>
        </Champ>

        <Champ etiquette="Valeur *" aide="Telle qu’écrite : 9,8 — &lt; 0,5 — négatif">
          <input className="champ" {...champ('valeur')} />
        </Champ>
        <Champ etiquette="Unité du compte rendu">
          <input
            className="champ"
            list="unites-biologie"
            placeholder={parametre?.unitesReconnues[0] ?? ''}
            {...champ('unite')}
          />
          <datalist id="unites-biologie">
            {(parametre?.unitesReconnues ?? []).map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </Champ>

        <Champ etiquette="Référence basse">
          <input className="champ" {...champ('refBasse')} />
        </Champ>
        <Champ etiquette="Référence haute">
          <input className="champ" {...champ('refHaute')} />
        </Champ>

        <Champ etiquette="Date du prélèvement *">
          <input className="champ" type="date" {...champ('datePrelevement')} />
        </Champ>
        <Champ etiquette="Heure">
          <input className="champ" placeholder="08h30" {...champ('heure')} />
        </Champ>
        <Champ etiquette="Laboratoire" className="sm:col-span-2">
          <input className="champ" {...champ('laboratoire')} />
        </Champ>

        {resultat && (
          <Champ etiquette="Raison de la correction" className="sm:col-span-2">
            <input className="champ" {...champ('raison')} />
          </Champ>
        )}

        {apercu && (
          <div className="sm:col-span-2">
            <Message ton={apercu.startsWith('Sera enregistré') ? 'info' : 'attention'}>
              {apercu}
            </Message>
          </div>
        )}

        {resultat?.documentId && (
          <p className="text-[12px] text-brume sm:col-span-2">
            Ce résultat vient d’un document. Le corriger n’efface ni le document ni la valeur
            d’origine : elle reste dans l’historique
            {resultat.valeurOriginale !== null
              ? ` (${resultat.valeurOriginale} ${resultat.uniteOriginale ?? ''})`
              : ''}
            .
          </p>
        )}

        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>
    </Modale>
  )
}
